import React, { useMemo, useState, useEffect } from 'react';
import { Calendar, User, Search, Tag } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { apiService } from '../services/api';

const Berita = () => {
  const { id } = useParams();
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.getNews();
        setNews(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Error loading news:', err);
        setError('Gagal memuat data berita.');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const categories = useMemo(() => ['all', ...new Set(news.map((item) => item.category).filter(Boolean))], [news]);

  const selectedNews = useMemo(() => {
    if (!id) return null;
    return news.find((item) => String(item.id) === String(id)) || null;
  }, [id, news]);

  const filteredNews = news.filter((item) => {
    const categoryMatch = categoryFilter === 'all' || item.category === categoryFilter;
    const searchMatch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       item.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return categoryMatch && searchMatch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <p className="text-gray-500 text-lg">Memuat berita...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <p className="text-red-600 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (id && selectedNews) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/berita" className="text-green-600 hover:text-green-700 font-semibold">
            ← Kembali ke daftar berita
          </Link>

          <article className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
            <img
              src={selectedNews.image}
              alt={selectedNews.title}
              className="w-full h-72 object-cover"
              onError={(e) => {
                e.currentTarget.src = 'https://via.placeholder.com/1200x700?text=IJSL+News';
              }}
            />
            <div className="p-6 md:p-8">
              <span className="inline-block bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold mb-4">
                {selectedNews.category}
              </span>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{selectedNews.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
                <span className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {new Date(selectedNews.date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {selectedNews.author}
                </span>
              </div>
              <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                {selectedNews.content || selectedNews.excerpt}
              </p>
            </div>
          </article>
        </div>
      </div>
    );
  }

  if (id && !selectedNews) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <p className="text-gray-500 text-lg">Berita tidak ditemukan.</p>
          <Link to="/berita" className="text-green-600 hover:text-green-700 font-semibold">
            Kembali ke daftar berita
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Berita</h1>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="inline h-4 w-4 mr-2" />
                Cari Berita
              </label>
              <input
                type="text"
                placeholder="Cari berita..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="inline h-4 w-4 mr-2" />
                Kategori
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'Semua Kategori' : cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNews.map((item) => (
            <article
              key={item.id}
              className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
            >
              <Link to={`/berita/${item.id}`}>
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-200"
                  />
                  <span className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {item.category}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between text-gray-500 text-sm mb-3">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(item.date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 hover:text-green-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3 mb-4">{item.excerpt}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <User className="h-4 w-4 mr-2" />
                    <span>{item.author}</span>
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>

        {filteredNews.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Tidak ada berita ditemukan.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Berita;